import { useEffect, useState } from "react";
import { useAutosaveOnboarding, useOnboardingProgress } from "@/lib/onboarding-progress";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { track } from "@/lib/analytics";
import {
  UserPlus, School, Wallet, Bus, HeartPulse, Sparkles,
  CheckCircle2, ChevronRight, ChevronLeft, Users,
} from "lucide-react";

type Step = { key: string; title: string; hint: string; icon: React.ComponentType<{ className?: string }> };
const STEPS: Step[] = [
  { key: "welcome",   title: "Welcome",         hint: "Two minutes to enrol your learner.", icon: Users },
  { key: "school",    title: "Pick a school",   hint: "Choose from Uganda's TriSphere network.", icon: School },
  { key: "learner",   title: "Learner details", hint: "Name, class and pocket money limits.", icon: UserPlus },
  { key: "transport", title: "Transport",       hint: "Optional — set pickup route & stop.", icon: Bus },
  { key: "wallet",    title: "Wallet & health", hint: "Kick-off balance and medical basics.", icon: HeartPulse },
  { key: "done",      title: "All set",         hint: "Review and enrol.", icon: Sparkles },
];

type Props = { guardianId: string; onDone: () => void; onCancel?: () => void };

export function LearnerOnboardingWizard({ guardianId, onDone, onCancel }: Props) {
  const qc = useQueryClient();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    school_id: "",
    full_name: "",
    class_label: "P4 · Day",
    boarding: false,
    student_no: "",
    daily_cap_ugx: 6000,
    balance_ugx: 20000,
    color_hex: "#7B1416",
    // Optional transport
    include_transport: false,
    bus_route: "Bus 14",
    pickup_stop: "Naalya Gate",
    pickup_time: "06:45",
    // Optional health note
    allergies: "",
  });
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm((f) => ({ ...f, [k]: v }));

  const { data: saved } = useOnboardingProgress("learner", null);
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    if (hydrated) return;
    if (saved && !saved.completed) {
      const st = saved.state as Partial<typeof form>;
      setForm((f) => ({ ...f, ...st }));
      setStep(saved.current_step ?? 0);
    }
    setHydrated(true);
  }, [saved, hydrated]);
  useAutosaveOnboarding("learner", step, STEPS.length, form as unknown as Record<string, unknown>, { enabled: hydrated });

  const { data: schools = [] } = useQuery({
    queryKey: ["onboard-schools"],
    queryFn: async () => (await (supabase.from as any)("schools_public").select("id,name,district,region").order("name")).data ?? [],
  });

  const enrol = useMutation({
    mutationFn: async () => {
      const initials = form.full_name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase() || "S";
      const { data: student, error } = await supabase.from("students").insert({
        school_id: form.school_id,
        guardian_id: guardianId,
        full_name: form.full_name.trim(),
        initials,
        color_hex: form.color_hex,
        student_no: form.student_no || `#${Math.floor(1000 + Math.random() * 9000)}`,
        class_label: form.class_label,
        boarding: form.boarding,
        bus_route: form.include_transport ? form.bus_route : "—",
        status_label: "Enrolled",
        status_dot: "success",
        status_location: "New profile",
        balance_ugx: form.balance_ugx,
        daily_cap_ugx: form.daily_cap_ugx,
      }).select("id").single();
      if (error) throw error;

      if (form.include_transport) {
        const rows = [1, 2, 3, 4, 5].flatMap((d) => ([
          { student_id: student.id, guardian_id: guardianId, route: form.bus_route, direction: "am", pickup_time: form.pickup_time, stop: form.pickup_stop, day_of_week: d },
          { student_id: student.id, guardian_id: guardianId, route: form.bus_route, direction: "pm", pickup_time: "16:15",           stop: "School gate",     day_of_week: d },
        ]));
        await supabase.from("transport_schedule").insert(rows);
      }
      if (form.allergies.trim()) {
        await supabase.from("health_visits").insert({
          student_id: student.id, guardian_id: guardianId,
          visit_date: new Date().toISOString(), reason: "Onboarding note", nurse_note: `Allergies: ${form.allergies}`, status: "resolved",
        });
      }
      return student.id;
    },
    onSuccess: (id) => {
      track("learner_enrolled", { student_id: id, school_id: form.school_id });
      toast.success(`${form.full_name} is enrolled 🎓`);
      qc.invalidateQueries();
      onDone();
    },
    onError: (e: Error) => toast.error(e.message ?? "Enrolment failed"),
  });

  const canAdvance = (() => {
    switch (STEPS[step].key) {
      case "school":  return !!form.school_id;
      case "learner": return form.full_name.trim().length >= 2;
      default: return true;
    }
  })();
  const progress = ((step + 1) / STEPS.length) * 100;
  const S = STEPS[step];

  return (
    <div className="grid gap-4 lg:grid-cols-[260px_1fr]">
      <Card className="p-4 bg-gradient-to-b from-amber-50 to-card">
        <Badge variant="secondary" className="gap-1"><UserPlus className="h-3 w-3" />Learner onboarding</Badge>
        <h2 className="mt-2 font-display text-xl">Enrol your learner</h2>
        <p className="mt-1 text-xs text-muted-foreground">6 quick steps · ≈ 2 minutes</p>
        <Progress value={progress} className="mt-3 h-1.5" />
        <ol className="mt-4 space-y-1">
          {STEPS.map((s, i) => {
            const done = i < step, active = i === step, Icon = s.icon;
            return (
              <li key={s.key}>
                <button disabled={i > step} onClick={() => setStep(i)}
                  className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm ${active ? "bg-primary/10 text-primary" : done ? "" : "text-muted-foreground"}`}>
                  <span className={`grid h-6 w-6 place-items-center rounded-full ${done ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
                    {done ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Icon className="h-3.5 w-3.5" />}
                  </span>
                  <span className="font-medium">{s.title}</span>
                </button>
              </li>
            );
          })}
        </ol>
      </Card>

      <Card className="flex min-h-[520px] flex-col p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
              <S.icon className="h-3.5 w-3.5" /> Step {step + 1} of {STEPS.length}
            </div>
            <h3 className="mt-1 font-display text-2xl">{S.title}</h3>
            <p className="text-sm text-muted-foreground">{S.hint}</p>
          </div>
          {onCancel && <Button variant="ghost" size="sm" onClick={onCancel}>Skip for now</Button>}
        </div>

        <div className="mt-6 flex-1">
          {S.key === "welcome" && (
            <div className="grid gap-3 md:grid-cols-3">
              {[
                { i: Wallet,     t: "Cashless pocket money", d: "Top up with MoMo. Set daily caps." },
                { i: Bus,        t: "Live transport",         d: "See the bus on a map." },
                { i: HeartPulse, t: "Health alerts",          d: "Every clinic visit, straight to you." },
              ].map(({ i: Icon, t, d }) => (
                <div key={t} className="rounded-xl border p-4">
                  <Icon className="h-5 w-5 text-primary" />
                  <div className="mt-2 font-medium">{t}</div>
                  <div className="text-xs text-muted-foreground">{d}</div>
                </div>
              ))}
            </div>
          )}

          {S.key === "school" && (
            <div className="space-y-3">
              <Field label="Search or pick school *">
                <Select value={form.school_id} onValueChange={(v) => set("school_id", v)}>
                  <SelectTrigger><SelectValue placeholder="Choose your child's school" /></SelectTrigger>
                  <SelectContent>
                    {schools.map((s: { id: string; name: string; district: string }) => (
                      <SelectItem key={s.id} value={s.id}>{s.name} · {s.district}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <p className="text-xs text-muted-foreground">Don't see your school? Ask the head-teacher to sign up — it's free to start.</p>
            </div>
          )}

          {S.key === "learner" && (
            <div className="grid gap-4 md:grid-cols-2">
              <Field label="Full name *"><Input value={form.full_name} onChange={(e) => set("full_name", e.target.value)} placeholder="e.g. Aisha Namubiru" /></Field>
              <Field label="Class"><Input value={form.class_label} onChange={(e) => set("class_label", e.target.value)} placeholder="P4 · Day" /></Field>
              <Field label="Boarding?">
                <RadioGroup value={form.boarding ? "yes" : "no"} onValueChange={(v) => set("boarding", v === "yes")} className="grid grid-cols-2 gap-2">
                  {["no","yes"].map(v => (
                    <label key={v} className={`cursor-pointer rounded-lg border p-2 text-center text-sm capitalize ${(form.boarding?"yes":"no")===v?"border-primary bg-primary/5":""}`}>
                      <RadioGroupItem value={v} className="sr-only" />{v === "yes" ? "Boarding" : "Day"}
                    </label>
                  ))}
                </RadioGroup>
              </Field>
              <Field label="Student number"><Input value={form.student_no} onChange={(e) => set("student_no", e.target.value)} placeholder="Auto if blank" /></Field>
              <Field label="Avatar accent">
                <div className="flex gap-2">
                  {["#7B1416","#A42022","#C4A882","#2C6E91","#7A3C7A","#3A6E4B"].map(c => (
                    <button key={c} onClick={() => set("color_hex", c)}
                      className={`h-8 w-8 rounded-full border-2 ${form.color_hex===c?"border-foreground":"border-transparent"}`}
                      style={{ background: c }} />
                  ))}
                </div>
              </Field>
            </div>
          )}

          {S.key === "transport" && (
            <div className="space-y-4">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.include_transport} onChange={(e) => set("include_transport", e.target.checked)} />
                Enrol {form.full_name || "learner"} on a bus route
              </label>
              {form.include_transport && (
                <div className="grid gap-4 md:grid-cols-3">
                  <Field label="Route"><Input value={form.bus_route} onChange={(e) => set("bus_route", e.target.value)} /></Field>
                  <Field label="Pickup stop"><Input value={form.pickup_stop} onChange={(e) => set("pickup_stop", e.target.value)} /></Field>
                  <Field label="Pickup time"><Input type="time" value={form.pickup_time} onChange={(e) => set("pickup_time", e.target.value)} /></Field>
                </div>
              )}
            </div>
          )}

          {S.key === "wallet" && (
            <div className="grid gap-4 md:grid-cols-2">
              <Field label="Opening wallet balance (UGX)"><Input type="number" value={form.balance_ugx} onChange={(e) => set("balance_ugx", parseInt(e.target.value || "0"))} /></Field>
              <Field label="Daily spend cap (UGX)"><Input type="number" value={form.daily_cap_ugx} onChange={(e) => set("daily_cap_ugx", parseInt(e.target.value || "0"))} /></Field>
              <Field label="Allergies / medical notes"><Input value={form.allergies} onChange={(e) => set("allergies", e.target.value)} placeholder="Peanuts, asthma, none…" /></Field>
              <div className="rounded-lg border bg-emerald-50/40 p-3 text-xs">
                Every spend books to the ledger. You'll see MoMo top-ups, canteen swipes and refunds in real time.
              </div>
            </div>
          )}

          {S.key === "done" && (
            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-xl border bg-gradient-to-br from-primary/10 to-card p-4">
                <div className="text-sm font-medium">Review</div>
                <dl className="mt-2 space-y-1 text-xs">
                  <Row k="Learner" v={form.full_name || "—"} />
                  <Row k="Class"   v={`${form.class_label}${form.boarding ? " · Boarding" : ""}`} />
                  <Row k="School"  v={schools.find((s: { id: string; name: string }) => s.id === form.school_id)?.name ?? "—"} />
                  <Row k="Bus"     v={form.include_transport ? `${form.bus_route} · ${form.pickup_stop} @ ${form.pickup_time}` : "None"} />
                  <Row k="Wallet"  v={`UGX ${form.balance_ugx.toLocaleString()} · cap ${form.daily_cap_ugx.toLocaleString()}/day`} />
                </dl>
              </div>
              <div className="rounded-xl border p-4">
                <div className="text-sm font-medium">What happens next</div>
                <ul className="mt-2 space-y-1.5 text-xs text-muted-foreground">
                  <li>✓ Learner profile created</li>
                  <li>✓ Wallet activated with opening balance</li>
                  <li>✓ Transport schedule Mon–Fri if enabled</li>
                  <li>✓ Parent notifications turned on</li>
                </ul>
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 flex items-center justify-between border-t pt-4">
          <Button variant="ghost" disabled={step === 0} onClick={() => setStep((s) => Math.max(0, s - 1))}>
            <ChevronLeft className="mr-1 h-4 w-4" />Back
          </Button>
          {step < STEPS.length - 1 ? (
            <Button disabled={!canAdvance} onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}>
              Continue<ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          ) : (
            <Button disabled={enrol.isPending || !form.full_name.trim() || !form.school_id} onClick={() => enrol.mutate()}>
              <Sparkles className="mr-1 h-4 w-4" />{enrol.isPending ? "Enrolling…" : "Enrol learner"}
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (<div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>{children}</div>);
}
function Row({ k, v }: { k: string; v: string }) {
  return (<div className="flex items-center justify-between gap-2 border-b border-dashed py-0.5"><dt className="text-muted-foreground">{k}</dt><dd className="truncate font-medium">{v}</dd></div>);
}
